export const faqCategories = [
  {
    slug: "cost-seg-basics",
    label: "Cost Seg Basics",
    items: [
      {
        q: "What is cost segregation?",
        a: "Cost segregation is the IRS-approved process of accelerating depreciation on a real estate asset by separating non-structural personal property (5- and 7-year depreciable life) and land improvements (15-year life) from real property (27.5- or 39-year life). With 100% bonus depreciation restored permanently by the One Big Beautiful Bill Act (OBBBA), most of those reclassified assets can be deducted in year one.",
      },
      {
        q: "What types of properties qualify for cost segregation?",
        a: "Virtually every type of property used for business or investment qualifies — multifamily, short-term rentals, office, retail, industrial, medical, hospitality, self-storage, and more. As a rule of thumb, the economics work best when depreciable basis (purchase price minus land) is above roughly $500K.",
      },
      {
        q: "Can cost segregation be done on a primary residence?",
        a: "No. Cost segregation cannot be performed on a primary residence. The IRS only allows cost segregation studies on income-producing or business-use properties, such as rental real estate or commercial buildings.",
      },
      {
        q: "Can you do a cost segregation study on a residential rental property?",
        a: "Yes. The IRS allows residential rental owners to break down building components into shorter depreciation categories — typically 5, 7, or 15 years — instead of the standard 27.5 years. Single-family rentals, duplexes, and small portfolios all qualify.",
      },
      {
        q: "How much should I expect to save with a cost segregation study?",
        a: "It depends on how much of the building's basis can be moved into shorter-life schedules and your personal tax rate. In our experience, 20–35% of basis frequently reclassifies into 5/7/15-year property. We model your expected savings for free before you commit to anything.",
      },
      {
        q: "Can I do a cost segregation study myself?",
        a: "Technically yes — if you're both an accountant and an engineer trained in the specific expertise of performing cost seg studies. In practice, the IRS prefers independent third-party studies, which significantly lessens the chance of a challenge. Every IncentTax study comes with audit defense — we fully stand behind our work.",
      },
    ],
  },
  {
    slug: "pricing-timing",
    label: "Pricing & Timing",
    items: [
      {
        q: "How much does a cost segregation study cost?",
        a: "Studies are fixed-fee, based on the size, scope, and complexity of the property. We never propose a study unless the modeled savings comfortably exceed the fee — usually by a factor of 10x or more — and the feasibility review is always free.",
      },
      {
        q: "Why don't you charge a percentage of the savings you find?",
        a: "The IRS specifically discourages contingency pricing, because it gives a cost segregation firm an incentive to unjustifiably push the limits on asset reclassification to drive up findings — and fees. Fixed-fee pricing keeps the study conservative, defensible, and aligned with your interests.",
      },
      {
        q: "What happens if you perform the study and don't find any savings?",
        a: "That situation is engineered out before it can happen. We run a no-cost feasibility review before any study is performed. If we don't believe there's enough value in our service for your property, we'll tell you so — before you spend a dollar.",
      },
      {
        q: "How long does it take to get the study completed?",
        a: "Most studies are completed in 30–60 days from kickoff, depending on the complexity of the property and how quickly we receive documents from you or your accountant. Look-back studies and portfolio engagements can extend slightly. We always confirm timing in writing before you sign.",
      },
      {
        q: "When is the best time for a cost segregation study?",
        a: "Ideally, before the initial depreciation schedule is filed — immediately after purchasing a building, or once a construction project is completed and placed in service. That said, it's rarely too late: look-back studies recover missed depreciation from prior years.",
      },
      {
        q: "Can I use cost segregation on buildings I placed in service in the past?",
        a: "Absolutely. 'Missed' depreciation from past purchases, construction, expansions, and renovations can be recouped via a look-back study. There's no need to amend prior returns — catch-up depreciation is taken in a single year by filing Form 3115 (Change in Accounting Method), which we prepare in-house.",
      },
      {
        q: "My property is 20 years old. Is cost segregation still beneficial?",
        a: "The age of the property is irrelevant — what matters is how long ago you purchased it. It's typically worthwhile to perform a study up to five years after acquisition, though several factors can move that number. We'll run a free feasibility review to show you the actual savings before you decide.",
      },
    ],
  },
  {
    slug: "working-with-your-cpa",
    label: "Working with Your CPA",
    items: [
      {
        q: "Doesn't my accountant already do cost segregation for me?",
        a: "Most probably not. While your CPA is highly qualified, cost segregation is a niche practice that requires qualified engineers and very specific expertise to produce a study that maximizes results and passes IRS scrutiny. Most accountants either outsource the work or refer clients to a specialist firm like IncentTax.",
      },
      {
        q: "How do I know my CPA will agree with the results of the study?",
        a: "We've worked alongside CPAs on every engagement and value their input — your accountant has the most comprehensive understanding of your tax situation. We work hand-in-hand with them throughout, and send a draft of the completed study to you and your accountant for review before finalizing it for your return.",
      },
      {
        q: "How do you work with my existing CPA?",
        a: "Seamlessly. We deliver everything your CPA needs — asset schedules, narratives, Form 3115 support, and depreciation tables. Most CPAs prefer to refer the engineering work to a specialist firm; we handle the study, they apply it to the return.",
      },
    ],
  },
  {
    slug: "tax-questions",
    label: "Tax Questions",
    items: [
      {
        q: "Will a cost segregation study put me at risk of an IRS audit?",
        a: "No. Cost segregation is a 100% IRS-approved method of depreciating investment real estate. As long as a qualified firm following the IRS Cost Segregation Audit Technique Guide performs the study, there is no additional audit risk. Every IncentTax study includes a detailed engineering report with photos, citations, and a defensible asset schedule — plus audit defense.",
      },
      {
        q: "What happens with recapture tax if I sell my property?",
        a: "All investment properties are subject to depreciation recapture upon sale — with or without a cost segregation study. The key: recapture rates are significantly lower than the top federal and state income tax rates the accelerated depreciation shielded during ownership, and the time value of having that cash in hand for years makes the study well worth it. A 1031 exchange can also defer recapture entirely.",
      },
      {
        q: "My property was part of a 1031 exchange. Can we still do cost segregation?",
        a: "Yes. However, there are multiple options for depreciating the replacement property, and the choice affects the value of the study. We coordinate directly with your tax advisor to determine the best path forward.",
      },
      {
        q: "Can cost segregation offset my W-2 income?",
        a: "Not directly in most cases — deductions apply to passive income from rental or investment properties. However, real estate professionals who qualify under IRS rules, or short-term rental owners who materially participate, may use depreciation from cost segregation to offset active income, including W-2 wages.",
      },
      {
        q: "How does the short-term rental loophole work?",
        a: "Short-term rentals (average guest stay of 7 days or less) are generally not treated as a 'rental activity' under IRS §469. If you materially participate — typically 100+ hours and more than anyone else — losses generated by cost segregation can offset active income, including W-2 wages. Cost segregation accelerates those losses into year one.",
      },
      {
        q: "What about long-term rentals?",
        a: "For long-term (>7 day average stay) rentals, depreciation deductions are passive and generally offset passive income from rentals and other passive activities. Unused passive losses carry forward indefinitely and can be unlocked at sale, on conversion to short-term, or if you qualify as a real estate professional.",
      },
    ],
  },
  {
    slug: "rd-tax-credit",
    label: "R&D Tax Credit",
    items: [
      {
        q: "What is the R&D tax credit?",
        a: "A dollar-for-dollar federal credit (with parallel credits in 35+ states) for companies that develop or improve products, processes, formulas, or software. It rewards technical work involving uncertainty resolved through experimentation — and it's one of the most underclaimed incentives in the tax code.",
      },
      {
        q: "Does the R&D credit apply to my industry?",
        a: "Probably. The credit applies to any work that develops or improves a product, process, formula, or software — and involves technical uncertainty resolved through experimentation. Manufacturing, software, architecture & engineering, food & beverage, life sciences, and many more industries qualify.",
      },
      {
        q: "What expenses qualify for the credit?",
        a: "Four buckets of qualified research expenses (QREs): employee wages for those performing or supervising research, supplies consumed in development, 65% of contract research payments, and cloud computing costs used for development environments.",
      },
      {
        q: "Can startups with no income tax liability use the credit?",
        a: "Yes. Qualified small businesses (under $5M in gross receipts, within five years of first revenue) can apply up to $500,000 per year of R&D credit against payroll taxes instead of income tax — real cash even when you're pre-profit.",
      },
      {
        q: "What changed with Section 174 amortization?",
        a: "From 2022–2024, businesses were forced to amortize domestic R&D expenses over five years instead of deducting them immediately — a major cash-flow hit. The One Big Beautiful Bill Act (OBBBA) restored immediate expensing of domestic R&D for tax years beginning in 2025, and small businesses may retroactively claim the prior years.",
      },
      {
        q: "What documentation do I need to claim the credit?",
        a: "Contemporaneous records that tie qualified activities to qualified expenses — project lists, technical narratives, payroll allocations, and testing records. We build the full substantiation package as part of every study, so your claim stands up to IRS scrutiny.",
      },
    ],
  },
];
